import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import CEAssignmentTable from "./CEAssignmentTable";
import BulkUploadModal from "../TE/BulkUploadModal";
import { ToastContainer, useToast } from "../common/Toast";
import { API_BASE } from "../../constants/urls";
import { uploadToS3 } from "../../utils/s3Uploader";

const CEDashboard = () => {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [stats, setStats] = useState(null);
  const { toasts, showSuccess, showError, removeToast, showWarning } =
    useToast();

  // Fetch assigned articles on component mount
  useEffect(() => {
    fetchAssignments();
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/ce/stats`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.success) setStats(data.stats);
      }
    } catch (e) { /* silently fail */ }
  };

  const fetchAssignments = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const response = await fetch(`${API_BASE}/api/ce/assigned-articles`, {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to fetch assignments");
      }

      const data = await response.json();
      setAssignments(data.articles || []);
    } catch (err) {
      console.error("Error fetching assignments:", err);
      setError(err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleSingleUpload = async (articleId, file, wordcount, onProgress) => {
    try {
      if (onProgress) onProgress(0);

      // 1. Upload to S3
      const s3Result = await uploadToS3(file, articleId, onProgress, 'ce-submissions');
      
      if (!s3Result.success) {
        throw new Error("S3 upload failed");
      }

      // 2. Record submission in backend
      const response = await fetch(`${API_BASE}/api/ce/submit-s3`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          article_id: articleId,
          s3_key: s3Result.s3_key,
          wordcount: wordcount,
          filename: file.name
        }),
      });

      const result = await response.json();

      if (response.ok) {
        await fetchAssignments(true);
        showSuccess(`✅ Submission recorded successfully!`);
        return { success: true };
      } else {
        showError(`❌ ${result.error || "Submission failed"}`);
        return { success: false, errors: [result.error] };
      }
    } catch (err) {
      console.error("Upload error:", err);
      showError(`❌ Error: ${err.message}`);
      return { success: false, errors: [err.message] };
    }
  };

  const handleBulkUpload = async (files, wordCounts, onProgress) => {
    try {
      const totalFiles = files.length;
      const submissions = []; // Collect all submissions for bulk endpoint

      // Step 1: Upload all files to S3 and collect submission data
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const articleId = file.name.split(".")[0];
        const wordcount = wordCounts?.[file.name];

        try {
          // Individual progress within the bulk context
          const s3Result = await uploadToS3(file, articleId, (percent) => {
             if (onProgress) {
                const overallProgress = Math.round(((i * 100) + percent) / totalFiles);
                onProgress(overallProgress);
             }
          }, 'ce-submissions');

          if (!s3Result.success) throw new Error("S3 upload failed");

          // Collect submission data
          submissions.push({
            article_id: articleId,
            s3_key: s3Result.s3_key,
            wordcount: wordcount,
            filename: file.name
          });
        } catch (err) {
          // Log upload error but continue with successful uploads
          console.error(`S3 upload failed for ${file.name}:`, err.message);
        }
      }

      // Step 2: Submit all collected S3 files via bulk endpoint
      if (submissions.length === 0) {
        showError("❌ No files were successfully uploaded to S3.");
        return { success: false, errors: ["No files uploaded"] };
      }

      const submitResponse = await fetch(`${API_BASE}/api/ce/submit-s3/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ submissions })
      });

      const submitResult = await submitResponse.json();

      await fetchAssignments(true);

      if (submitResult.successful_count > 0 && submitResult.failed_count === 0) {
        showSuccess(`✅ Successfully uploaded ${submitResult.successful_count} articles!`);
      } else if (submitResult.successful_count > 0) {
        showWarning(`⚠️ Partial success: ${submitResult.successful_count} uploaded, ${submitResult.failed_count} failed.`);
      } else {
        showError("❌ All submissions failed. Wordcount data is preserved for retry.");
      }

      return {
        success: submitResult.failed_count === 0,
        successful_uploads: submitResult.successful_submissions || [],
        failed_uploads: submitResult.failed_submissions || []
      };
    } catch (err) {
      console.error("Bulk upload error:", err);
      return { success: false, errors: [err.message] };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4 border-purple-500"></div>
          <p className="text-purple-600 font-medium">
            Loading your assignments...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <strong className="font-bold">Error!</strong>
            <span className="block sm:inline"> {error}</span>
          </div>
          <button
            onClick={fetchAssignments}
            className="mt-4 bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Upload Articles
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Welcome back, {user?.username} (Copy Editor)
            </p>
          </div>
          <button
            onClick={() => setShowBulkUpload(true)}
            className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2.5 px-6 rounded-xl flex items-center shadow-lg shadow-purple-200 transition-all hover:scale-105 active:scale-95"
          >
            <span className="mr-2">📁</span>
            Bulk Upload
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Assigned', value: stats.total_assigned, color: 'bg-blue-50 border-blue-200 text-blue-700', icon: '📋' },
            { label: 'In Progress', value: stats.wip, color: 'bg-yellow-50 border-yellow-200 text-yellow-700', icon: '⚙️' },
            { label: 'Submitted', value: stats.submitted, color: 'bg-green-50 border-green-200 text-green-700', icon: '✅' },
            { label: 'Total Wordcount', value: stats.total_wordcount.toLocaleString(), color: 'bg-purple-50 border-purple-200 text-purple-700', icon: '📝' },
          ].map(({ label, value, color, icon }) => (
            <div key={label} className={`rounded-xl border p-4 flex flex-col items-center text-center ${color}`}>
              <span className="text-2xl mb-1">{icon}</span>
              <span className="text-2xl font-bold">{value}</span>
              <span className="text-xs font-medium mt-1 opacity-80">{label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Main Content */}
      <div className="bg-white shadow-xl rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900 font-display">
              Assigned Articles ({assignments.length})
            </h2>
            <button
              onClick={fetchAssignments}
              className="text-purple-600 hover:text-purple-900 text-sm font-semibold flex items-center transition-colors"
            >
              <span className="mr-1">🔄</span>
              Refresh List
            </button>
          </div>

          <CEAssignmentTable
            assignments={assignments}
            onSingleUpload={handleSingleUpload}
          />
        </div>
      </div>

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <BulkUploadModal
          assignments={assignments}
          onClose={() => setShowBulkUpload(false)}
          onUpload={handleBulkUpload}
        />
      )}

      {/* Floating Notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default CEDashboard;
